import qrcode from 'qrcode-generator';

import { createMatrix, type QrMatrix } from './matrix';
import type { ErrorCorrectionLevel } from './types';

/** Matches the cap the (previously unused) validator advertised to users. */
export const MAX_CONTENT_LENGTH = 2000;

export type QrGenerationError =
  | { readonly kind: 'empty' }
  | { readonly kind: 'too-long'; readonly length: number; readonly max: number }
  | { readonly kind: 'capacity' };

export type QrGenerationResult =
  | { readonly ok: true; readonly matrix: QrMatrix }
  | { readonly ok: false; readonly error: QrGenerationError };

/**
 * Builds the module matrix for `content`.
 *
 * Failure is returned, never swallowed. The previous version caught everything
 * and returned an empty matrix, which the canvas then skipped rendering — so a
 * too-long input left the *previous* QR code on screen and downloadable.
 */
export function generateQrMatrix(
  content: string,
  errorCorrectionLevel: ErrorCorrectionLevel,
): QrGenerationResult {
  if (content.trim().length === 0) {
    return { ok: false, error: { kind: 'empty' } };
  }

  if (content.length > MAX_CONTENT_LENGTH) {
    return {
      ok: false,
      error: { kind: 'too-long', length: content.length, max: MAX_CONTENT_LENGTH },
    };
  }

  try {
    // Type 0 lets the library pick the smallest version that fits the payload.
    const qr = qrcode(0, errorCorrectionLevel);
    qr.addData(content);
    qr.make();

    const size = qr.getModuleCount();
    return { ok: true, matrix: createMatrix(size, (row, col) => qr.isDark(row, col)) };
  } catch {
    // The library throws only when the payload exceeds capacity at this level.
    return { ok: false, error: { kind: 'capacity' } };
  }
}

/** User-facing German copy for a generation failure. */
export function describeQrError(error: QrGenerationError): string {
  switch (error.kind) {
    case 'empty':
      return 'Gib einen Inhalt ein, um einen QR-Code zu erzeugen.';
    case 'too-long':
      return `Der Inhalt ist zu lang (${String(error.length)} von maximal ${String(error.max)} Zeichen).`;
    case 'capacity':
      return 'Der Inhalt passt nicht in einen QR-Code. Kürze den Text oder wähle eine niedrigere Fehlerkorrektur.';
  }
}
